import React, { useCallback, useEffect } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';

import { Button, Stack, TextField } from '@mui/material';

import useDebounced from '~/hooks/useDebounced';

import isUUID from '~/utils/isUUID';

export interface CreditReversalOrderSearchCriteria {
  bookingNumber?: string;
  orderId?: string;
}

interface SearchFormFields {
  bookingNumber: string;
  orderId: string;
}

interface Props {
  onSearchChange: (criteria: CreditReversalOrderSearchCriteria | null) => void;
  resetKey: number;
}

const EMPTY_SEARCH_FORM: SearchFormFields = {
  bookingNumber: '',
  orderId: '',
};

const SEARCH_DEBOUNCE_MS = 300;

export default function CreditReversalOrderSearch({ onSearchChange, resetKey }: Props) {
  const { control, reset } = useForm<SearchFormFields>({
    defaultValues: EMPTY_SEARCH_FORM,
  });
  const orderIdSearchTerm = useWatch({ control, name: 'orderId' }) ?? '';
  const bookingNumberSearchTerm = useWatch({ control, name: 'bookingNumber' }) ?? '';

  const orderId = orderIdSearchTerm.trim();
  const bookingNumber = bookingNumberSearchTerm.trim();
  const isOrderIdValid = !orderId || isUUID(orderId);
  const hasSearchValue = Boolean(orderId || bookingNumber);
  const debouncedOrderId = useDebounced(orderId, SEARCH_DEBOUNCE_MS);
  const debouncedBookingNumber = useDebounced(bookingNumber, SEARCH_DEBOUNCE_MS);
  const isDebouncedOrderIdValid = !debouncedOrderId || isUUID(debouncedOrderId);
  const hasDebouncedSearchValue = Boolean(debouncedOrderId || debouncedBookingNumber);

  useEffect(() => {
    if (!hasDebouncedSearchValue || !isDebouncedOrderIdValid) {
      onSearchChange(null);
      return;
    }

    onSearchChange({
      bookingNumber: debouncedBookingNumber || undefined,
      orderId: debouncedOrderId || undefined,
    });
  }, [debouncedBookingNumber, debouncedOrderId, hasDebouncedSearchValue, isDebouncedOrderIdValid, onSearchChange]);

  useEffect(() => {
    reset(EMPTY_SEARCH_FORM);
    onSearchChange(null);
  }, [onSearchChange, reset, resetKey]);

  const handleClear = useCallback(() => {
    reset(EMPTY_SEARCH_FORM);
    onSearchChange(null);
  }, [onSearchChange, reset]);

  return (
    <Stack
      component="form"
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      sx={{ marginY: 1 }}
    >
      <Controller
        control={control}
        name="orderId"
        render={({ field }) => (
          <TextField
            {...field}
            disabled={Boolean(bookingNumber)}
            error={!isOrderIdValid}
            label="Search Order ID"
            helperText={!isOrderIdValid ? 'Enter a full Order ID UUID' : 'Search by order ID within this customer'}
            size="small"
            fullWidth
          />
        )}
      />
      <Controller
        control={control}
        name="bookingNumber"
        render={({ field }) => (
          <TextField
            {...field}
            disabled={Boolean(orderId)}
            label="Search Booking Number"
            helperText="Search within this customer"
            size="small"
            fullWidth
          />
        )}
      />
      {hasSearchValue && (
        <Button type="button" variant="text" onClick={handleClear}>
          Clear
        </Button>
      )}
    </Stack>
  );
}
